import React, { useRef, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Camera,
  RotateCcw,
  Check,
  X,
  ImageIcon,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const PhotoCapture = ({ 
  onCapture, 
  onClear,
  title = "Delivery Photo",
  subtitle = "Take a photo as proof of delivery",
  className = '' 
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // back camera

  const startCamera = useCallback(async () => {
    setIsLoading(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
      }
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Camera Error', { description: 'Could not access camera' });
    }
    setIsLoading(false);
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setPhoto(dataUrl);
      stopCamera();
      onCapture?.(dataUrl);
    }
  };

  const retakePhoto = () => {
    setPhoto(null);
    onClear?.();
    startCamera();
  };

  const switchCamera = async () => {
    stopCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    setTimeout(() => startCamera(), 100);
  };

  return (
    <Card className={`border-2 border-gray-200 ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Camera className="h-4 w-4 text-green-500" />
          {title}
        </CardTitle>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" />
        
        {/* Photo Preview or Camera */}
        <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
          {photo ? (
            <>
              <img 
                src={photo} 
                alt="Captured" 
                className="w-full h-full object-cover"
                data-testid="captured-photo"
              />
              <Badge className="absolute top-2 left-2 bg-green-500">
                <Check className="h-3 w-3 mr-1" />
                Captured
              </Badge>
            </>
          ) : stream ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                data-testid="camera-preview"
              />
              <Badge className="absolute top-2 left-2 bg-red-500 animate-pulse">
                <span className="h-2 w-2 rounded-full bg-white mr-1" />
                Live
              </Badge>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              {isLoading ? (
                <Loader2 className="h-12 w-12 animate-spin opacity-50" />
              ) : (
                <>
                  <ImageIcon className="h-12 w-12 opacity-50 mb-2" />
                  <p className="text-sm opacity-70">No photo captured</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {photo ? (
            <>
              <Button
                variant="outline"
                onClick={retakePhoto}
                className="flex-1 gap-2"
                data-testid="retake-photo-btn"
              >
                <RotateCcw className="h-4 w-4" />
                Retake
              </Button>
              <Button
                className="flex-1 gap-2 bg-green-500 hover:bg-green-600"
                disabled
                data-testid="photo-accepted"
              >
                <Check className="h-4 w-4" />
                Accepted
              </Button>
            </>
          ) : stream ? (
            <>
              <Button
                variant="outline"
                onClick={stopCamera}
                className="flex-1 gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                onClick={capturePhoto}
                className="flex-1 gap-2 bg-green-500 hover:bg-green-600"
                data-testid="capture-btn"
              >
                <Camera className="h-4 w-4" />
                Capture
              </Button>
            </>
          ) : (
            <Button
              onClick={startCamera}
              className="w-full gap-2 bg-black hover:bg-gray-800"
              disabled={isLoading}
              data-testid="start-camera-btn"
            >
              <Camera className="h-4 w-4" />
              {isLoading ? 'Starting...' : 'Open Camera'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PhotoCapture;
